import { Component, HostListener, Inject, OnDestroy, OnInit, Renderer2, DOCUMENT, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet, ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';

import { map, take, filter, shareReplay } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { AnalyticsService } from './core/analytics';
import * as fromRoot from './reducers';
import { routeTransitionAnimations } from './route-animations';
import { SharedConstants } from './shared/shared.constants';
import { AppConstants } from './app.constants';
import { MediaObserver } from 'ngx-flexible-layout';
import * as layout from './actions/layout';
import { RefreshService } from './core/refresh.service';
import { DebugLoggerService } from './core/debug-logger.service';
import { CfaIconsService } from './shared/services/cfa-icons.service';
import { LOCATION_ERROR_PATH, stripLocationPrefix } from './shared/navbar/location-utils';
import { readSidebarCollapsed } from './shared/sidebar/sidebar-collapsed-storage';

export const browserRefreshed = { value: false };

@Component({
  selector: 'spotlight-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [routeTransitionAnimations],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  private static readonly darkModeStorageKey = 'spotlight_dark_mode';

  public refreshSubscription: Subscription;
  public acceptedEula$: Observable<boolean>;
  public maintenance$: Observable<boolean>;
  public isNextGen$: Observable<boolean>;
  public disableDiscoverTab$: Observable<boolean>;
  public disableMeetTab$: Observable<boolean>;
  public disableRecoverTab$: Observable<boolean>;
  public disableEngageTab$: Observable<boolean>;
  public disableCalendarTab$: Observable<boolean>;
  public locationLoading$: Observable<boolean>;
  public darkModeEnabled$: Observable<boolean>;
  public isMobile$: Observable<boolean>;
  public minimumPageHeight$: Observable<number>;
  public userError$: Observable<string | undefined>;
  public locationError$: Observable<string | undefined>;
  public eulaError$: Observable<boolean>;
  public isLoggedOutRoute: boolean = false;
  public isLocationErrorRoute: boolean = false;
  public isCampaignDetailsRoute: boolean = false;
  public isBlueBackground: boolean = false;
  public sidebarCollapsed: boolean = readSidebarCollapsed();
  // Navbar properties for legacy UI
  public showLocationNumber: boolean = false;
  public showSelectedLocationName: boolean = false;
  public selectedNavIndex: number = 0;
  private navigationEndSubscription?: Subscription;
  private isLoggedOutRouteSubscription?: Subscription;
  private readonly navigationEnd$: Observable<NavigationEnd>;
  private mediaSubscription?: Subscription;
  private darkModeSubscription?: Subscription;

  constructor(private readonly router: Router,
              private readonly store: Store<fromRoot.State>,
              private readonly analytics: AnalyticsService,
              private readonly activatedRoute: ActivatedRoute,
              private readonly mediaObserver: MediaObserver,
              private readonly refreshService: RefreshService,
              private readonly debugLogger: DebugLoggerService,
              private readonly renderer: Renderer2,
              private readonly cfaIconsService: CfaIconsService,
              @Inject(DOCUMENT) private readonly document: Document) {
    // Register CFA Design System icons
    this.cfaIconsService.registerIcons();

    this.acceptedEula$ = this.store.select(fromRoot.getEulaAgree);
    this.maintenance$ = this.store.select(fromRoot.getFeatureEnabled(AppConstants.maintenanceFlag));
    this.isNextGen$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.displaySidenav));
    this.disableDiscoverTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableDiscoverTabFlag));
    this.disableMeetTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableMeetTabFlag));
    this.disableRecoverTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableRecoverTabFlag));
    this.disableEngageTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableEngageTabFlag));
    this.disableCalendarTab$ = this.store.select(fromRoot.getFeatureEnabled(SharedConstants.disableCalendarTabFlag));
    this.userError$ = this.store.select(fromRoot.getUserError);
    this.locationError$ = this.store.select(fromRoot.getLocationError);
    this.eulaError$ = this.store.select(fromRoot.getEulaError);
    this.locationLoading$ = this.store.select(fromRoot.getUserLoading);
    this.darkModeEnabled$ = this.store.select(fromRoot.getDarkModeEnabled);
    this.isMobile$ = this.store.select(fromRoot.getIsMobile);

    // Shared NavigationEnd stream to avoid redundant independent subscriptions to router.events
    this.navigationEnd$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // If it's mobile, set minimum height to allow room for the full route animation
    this.minimumPageHeight$ = this.isMobile$.pipe(map((isMobile) => {
      return isMobile ? window.screen.height : 400;
    }));

    this.refreshSubscription = router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefreshed.value = !router.navigated;
        refreshService.setWasRefreshed(browserRefreshed.value);
      }
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        this.routingLogic(url);
      }
    });

  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Toggle debug logging with Ctrl+Shift+L (works in all browsers on all platforms)
    if ((event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'L' || event.key === 'l')) {
      event.preventDefault();
      this.debugLogger.toggle();
    }
  }

  ngOnInit() {
    this.mediaSubscription = this.mediaObserver
      .asObservable()
      .subscribe((change) => {
        this.store.dispatch(new layout.ChangedViewSizeAction(change[0].mqAlias))
      });

    this.navigationEndSubscription = this.navigationEnd$
      .subscribe((event) => {
        // root -> :locationNumber (or a non-location gate like eula/maintenance) -> feature -> feature's own leaf route
        this.isBlueBackground = this.activatedRoute.root.firstChild?.firstChild?.firstChild?.snapshot?.data?.isBlueBackground ?? false;

        // Update navbar properties based on current route
        this.updateNavbarPropertiesFromRoute(event.urlAfterRedirects);
      });

    // Track if we're on the logged-out route
    this.isLoggedOutRoute = this.router.url.startsWith('/logged-out');
    this.isLocationErrorRoute = this.router.url.startsWith(LOCATION_ERROR_PATH);
    this.isLoggedOutRouteSubscription = this.navigationEnd$
      .subscribe((event) => {
        this.isLoggedOutRoute = event.urlAfterRedirects.startsWith('/logged-out');
        this.isLocationErrorRoute = event.urlAfterRedirects.startsWith(LOCATION_ERROR_PATH);
      });

    // Initialize navbar properties for the current route
    this.updateNavbarPropertiesFromRoute(this.router.url);

    this.darkModeSubscription = this.darkModeEnabled$
      .subscribe((isDarkModeEnabled) => {
        if (isDarkModeEnabled) {
          this.renderer.addClass(this.document.body, 'spotlight-dark-mode');
          this.renderer.addClass(this.document.documentElement, 'spotlight-dark-mode-preload');
        } else {
          this.renderer.removeClass(this.document.body, 'spotlight-dark-mode');
          this.renderer.removeClass(this.document.documentElement, 'spotlight-dark-mode-preload');
        }

        try {
          localStorage.setItem(AppComponent.darkModeStorageKey, String(isDarkModeEnabled));
        } catch {
          // Ignore storage failures and continue applying runtime classes.
        }
      });

    // Clear refresh reason after initialization is complete
    setTimeout(() => {
      this.refreshService.clearRefreshReason();
    }, 5000);
  }

  /** Routes that render without the app chrome (sidebar, navbar, user error banner). */
  public get isStandaloneRoute(): boolean {
    return this.isLoggedOutRoute || this.isLocationErrorRoute;
  }

  private updateNavbarPropertiesFromRoute(rawUrl: string): void {
    // Determine selected nav index and display options based on route
    const url = stripLocationPrefix(rawUrl);
    this.isCampaignDetailsRoute = /^\/engage\/campaign-details(?:[/?#;]|$)/.test(url);
    if (url.startsWith('/discover')) {
      this.selectedNavIndex = 0;
      this.showSelectedLocationName = true;
      this.showLocationNumber = false;
    } else if (url.startsWith('/meet') || url.startsWith('/understand')) {
      this.selectedNavIndex = 1;
      this.showSelectedLocationName = false;
      this.showLocationNumber = true;
    } else if (url.startsWith('/care')) {
      this.selectedNavIndex = 2;
      this.showSelectedLocationName = false;
      this.showLocationNumber = true;
    } else if (url.startsWith('/engage')) {
      this.selectedNavIndex = 3;
      this.showSelectedLocationName = false;
      this.showLocationNumber = true;
    } else if (url.startsWith('/calendar')) {
      this.selectedNavIndex = 4;
      this.showSelectedLocationName = false;
      this.showLocationNumber = false;
    } else {
      // Default
      this.showSelectedLocationName = false;
      this.showLocationNumber = false;
    }
  }

  public routingLogic(url: string): void {
    const strippedUrl = stripLocationPrefix(url);
    this.acceptedEula$.pipe(take(1)).subscribe((accepted) => {
      this.maintenance$.pipe(take(1)).subscribe((activeMaintenance) => {
        if (!accepted) {
          this.router.navigate(['/eula']);
        } else if (activeMaintenance && !(strippedUrl.includes('/care')
          || strippedUrl.includes('/menu') || strippedUrl.includes('/settings') || strippedUrl.includes('/notifications'))) {
          this.router.navigate(['/maintenance']);
        } else if ((accepted && strippedUrl === '/eula') || strippedUrl === '/maintenance') {
          this.router.navigate(['/discover']);
        }
      });

      // Only check disabled tabs if the user has accepted the EULA
      if (accepted) {
        this.checkDisabledTabsAndRedirect(strippedUrl);
      }
    });

    this.analytics.logPageView(url, false);
  }

  private checkDisabledTabsAndRedirect(url: string) {
    // If a tab is disabled then redirect the user to the next enabled one
    this.disableDiscoverTab$.pipe(take(1)).subscribe((disableDiscoverTab) => {
      this.disableMeetTab$.pipe(take(1)).subscribe((disableMeetTab) => {
        this.disableRecoverTab$.pipe(take(1)).subscribe((disableRecoverTab) => {
          this.handleTabRedirection(url, disableDiscoverTab, disableMeetTab, disableRecoverTab);
        });
      });
    });
  }

  private handleTabRedirection(url: string, disableDiscoverTab: boolean, disableMeetTab: boolean, disableRecoverTab: boolean) {
    this.disableEngageTab$.pipe(take(1)).subscribe((disableEngageTab) => {
      this.disableCalendarTab$.pipe(take(1)).subscribe((disableCalendarTab) => {
        if (disableDiscoverTab && !disableMeetTab && url === '/discover') {
          this.router.navigate(['/meet']);
        } else if (disableDiscoverTab && disableMeetTab && !disableRecoverTab && url === '/discover') {
          this.router.navigate(['/care']);
        } else if (disableDiscoverTab && disableMeetTab
          && disableRecoverTab && !disableEngageTab && url === '/discover') {
          this.router.navigate(['/engage/template-selection']);
        } else if (disableDiscoverTab && disableMeetTab
          && disableRecoverTab && disableEngageTab && !disableCalendarTab && url === '/discover') {
          this.router.navigate(['/calendar']);
        } else if (disableDiscoverTab && disableMeetTab
          && disableRecoverTab && disableEngageTab && disableCalendarTab) {
          // If every tab is disabled, route to the maintenance page
          this.router.navigate(['/maintenance']);
        } else if (disableDiscoverTab && disableMeetTab && disableEngageTab && disableCalendarTab
            && !(url.includes('/care') || url.includes('/customer'))) {
          if (!url.includes('/menu') || url.includes('/settings')
            || url.includes('/notifications')) {
            this.router.navigate(['/care']);
          }
        }
      });
    });
  }

  public prepareRoute$(outlet: RouterOutlet) {
    return this.isMobile$.pipe(map(isMobile => {
      return isMobile && outlet?.activatedRouteData?.['animationState'];
    }));
  }

  public onSidebarToggle(isCollapsed: boolean) {
    this.sidebarCollapsed = isCollapsed;
  }

  ngOnDestroy() {
    this.refreshSubscription.unsubscribe();
    this.navigationEndSubscription?.unsubscribe();
    this.isLoggedOutRouteSubscription?.unsubscribe();
    this.mediaSubscription?.unsubscribe();
    this.darkModeSubscription?.unsubscribe();
  }
}
